import { db } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import stripeApiService from './stripeApiService';

/**
 * Service de synchronisation Stripe/Firestore
 * Met en cache les produits Stripe dans Firestore pour améliorer les performances
 */
class StripeSyncService {
  constructor() {
    this.cacheCollection = 'stripeProductsCache';
    this.lastSyncKey = 'stripeProductsLastSync';
  }

  /**
   * Récupère les produits Stripe avec mise en cache
   */
  async getProductsWithCache() {
    try {
      // Vérifier si le cache est récent (moins de 5 minutes)
      const shouldUseCache = await this.shouldUseCache();
      
      if (shouldUseCache) {
        console.log('📦 Utilisation du cache Firestore pour les produits Stripe');
        const cachedProducts = await this.getCachedProducts();
        if (cachedProducts && cachedProducts.length > 0) {
          return cachedProducts;
        }
      }

      // Récupérer les produits depuis Stripe
      console.log('🔄 Récupération des produits depuis Stripe API');
      const products = await stripeApiService.getProductsWithPrices();
      
      // Mettre à jour le cache
      await this.updateCache(products);
      
      return products;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des produits avec cache:', error);
      
      // En cas d'erreur, essayer de récupérer depuis le cache même s'il est ancien
      const cachedProducts = await this.getCachedProducts();
      if (cachedProducts && cachedProducts.length > 0) {
        console.log('⚠️ Utilisation du cache de secours');
        return cachedProducts;
      }
      
      throw error;
    }
  }

  /**
   * Récupère les produits Stripe en forçant le rafraîchissement du cache
   */
  async getProductsWithForcedRefresh() {
    try {
      console.log('🔄 Forçage du rafraîchissement du cache Stripe');
      const products = await stripeApiService.getProductsWithPrices();
      
      // Mettre à jour le cache
      await this.updateCache(products);
      
      console.log('✅ Cache rafraîchi avec succès');
      return products;
    } catch (error) {
      console.error('❌ Erreur lors du rafraîchissement forcé:', error);
      
      // En cas d'erreur, essayer de récupérer depuis le cache existant
      const cachedProducts = await this.getCachedProducts();
      if (cachedProducts && cachedProducts.length > 0) {
        console.log('⚠️ Utilisation du cache existant après erreur');
        return cachedProducts;
      }
      
      throw error;
    }
  }

  /**
   * Vérifie si le cache est récent
   */
  async shouldUseCache() {
    try {
      const lastSyncDoc = await getDoc(doc(db, this.cacheCollection, this.lastSyncKey));
      
      if (!lastSyncDoc.exists()) {
        return false;
      }

      const lastSync = lastSyncDoc.data().timestamp;
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000; // 5 minutes en millisecondes

      return (now - lastSync) < fiveMinutes;
    } catch (error) {
      console.error('❌ Erreur lors de la vérification du cache:', error);
      return false;
    }
  }

  /**
   * Récupère les produits depuis le cache Firestore
   */
  async getCachedProducts() {
    try {
      const querySnapshot = await getDocs(collection(db, this.cacheCollection));
      const products = [];

      querySnapshot.forEach((doc) => {
        // Ignorer le document de synchronisation
        if (doc.id !== this.lastSyncKey) {
          products.push(doc.data());
        }
      });

      return products;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du cache:', error);
      return null;
    }
  }

  /**
   * Met à jour le cache Firestore
   */
  async updateCache(products) {
    try {
      console.log('💾 Mise à jour du cache Firestore...');
      
      // Mettre à jour chaque produit
      const updatePromises = products.map(product => 
        setDoc(doc(db, this.cacheCollection, product.id), {
          ...product,
          cachedAt: Date.now()
        })
      );

      // Mettre à jour le timestamp de synchronisation
      updatePromises.push(
        setDoc(doc(db, this.cacheCollection, this.lastSyncKey), {
          timestamp: Date.now(),
          productCount: products.length
        })
      );

      await Promise.all(updatePromises);
      console.log('✅ Cache Firestore mis à jour avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du cache:', error);
      throw error;
    }
  }

  /**
   * Force la synchronisation des produits Stripe
   */
  async forceSync() {
    try {
      console.log('🔄 Synchronisation forcée des produits Stripe...');
      const products = await stripeApiService.getProductsWithPrices();
      await this.updateCache(products);
      console.log('✅ Synchronisation forcée terminée');
      return products;
    } catch (error) {
      console.error('❌ Erreur lors de la synchronisation forcée:', error);
      throw error;
    }
  }

  /**
   * Récupère les produits par catégorie et cible avec fallback pour les produits configurés
   */
  async getProductsByCategoryAndTarget(category, target) {
    try {
      const products = await this.getProductsWithCache();
      
      // Récupérer les produits configurés pour cette catégorie
      const configuredProductIds = await this.getConfiguredProductIdsForCategory(category);
      
      return products.filter(product => {
        // Inclure les produits avec métadonnées correspondantes
        const hasMatchingMetadata = 
          product.metadata?.category === category && 
          product.metadata?.target === target &&
          product.active;
        
        // Inclure les produits explicitement configurés
        const isConfigured = configuredProductIds.includes(product.id);
        
        return hasMatchingMetadata || isConfigured;
      });
    } catch (error) {
      console.error('❌ Erreur lors du filtrage des produits:', error);
      throw error;
    }
  }

  /**
   * Récupère les IDs des produits configurés pour une catégorie spécifique
   */
  async getConfiguredProductIdsForCategory(category) {
    try {
      // Import dynamique pour éviter les dépendances circulaires
      const stripeProductsConfigService = await import('./stripeProductsConfigService.js');
      
      if (category === 'subscription') {
        return await stripeProductsConfigService.default.getSubscriptionProductIds();
      } else if (category === 'billing') {
        return await stripeProductsConfigService.default.getInvoiceProductIds();
      }
      
      return [];
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des produits configurés:', error);
      return [];
    }
  }

  /**
   * Récupère les produits pour les abonnements intervenants
   */
  async getSubscriptionProducts() {
    return this.getProductsByCategoryAndTarget('subscription', 'employee');
  }

  /**
   * Récupère les produits pour la facturation bénéficiaire
   */
  async getBillingProducts() {
    return this.getProductsByCategoryAndTarget('billing', 'beneficiary');
  }

  /**
   * Récupère les produits pour les services
   */
  async getServiceProducts() {
    return this.getProductsByCategoryAndTarget('service', 'both');
  }

  /**
   * Vide le cache
   */
  async clearCache() {
    try {
      console.log('🗑️ Vidage du cache Firestore...');
      
      const querySnapshot = await getDocs(collection(db, this.cacheCollection));
      const deletePromises = [];

      querySnapshot.forEach((doc) => {
        deletePromises.push(doc.ref.delete());
      });

      await Promise.all(deletePromises);
      console.log('✅ Cache Firestore vidé avec succès');
    } catch (error) {
      console.error('❌ Erreur lors du vidage du cache:', error);
      throw error;
    }
  }

  /**
   * Obtient les statistiques du cache
   */
  async getCacheStats() {
    try {
      const lastSyncDoc = await getDoc(doc(db, this.cacheCollection, this.lastSyncKey));
      const querySnapshot = await getDocs(collection(db, this.cacheCollection));
      
      const stats = {
        totalProducts: querySnapshot.size - (lastSyncDoc.exists() ? 1 : 0),
        lastSync: lastSyncDoc.exists() ? new Date(lastSyncDoc.data().timestamp) : null,
        cacheEnabled: true
      };

      return stats;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des statistiques du cache:', error);
      return {
        totalProducts: 0,
        lastSync: null,
        cacheEnabled: false
      };
    }
  }
}

// Instance singleton
const stripeSyncService = new StripeSyncService();

export default stripeSyncService;
